import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Tooltip, Bar, Legend } from 'recharts';

// 1. 🧪 GRAPH LOADING SKELETON
export const GraphLoadingSkeleton = () => {
    return (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 animate-pulse">
            <div className="flex flex-col gap-y-4 mb-8">
                <div>
                    <div className="h-3 w-32 bg-slate-200 dark:bg-slate-800 rounded-md mb-2"></div>
                    <div className="h-6 w-44 bg-slate-200 dark:bg-slate-800 rounded-md"></div>
                </div>
                <div className="h-8 w-24 bg-slate-100 dark:bg-slate-800/60 rounded-full self-end"></div>
            </div>
            <div className="space-y-4">
                <div className="h-[240px] flex items-end justify-between px-4 pt-4 border-b border-l border-slate-100 dark:border-slate-900">
                    <div className="h-[60%] w-6 bg-slate-200/70 dark:bg-slate-800/60 rounded-t-sm"></div>
                    <div className="h-[40%] w-6 bg-slate-200/70 dark:bg-slate-800/60 rounded-t-sm"></div>
                    <div className="h-[75%] w-6 bg-slate-200/70 dark:bg-slate-800/60 rounded-t-sm"></div>
                    <div className="h-[50%] w-6 bg-slate-200/70 dark:bg-slate-800/60 rounded-t-sm"></div>
                </div>
            </div>
        </div>
    );
};

// 2. 🎨 DYNAMIC CORNER ROUNDING LOGIC
const DynamicGlassBar = (props) => {
    const { x, y, width, height, fill, stroke, strokeWidth, payload } = props;
    if (!height || height <= 0) return null;

    const radius = 6;
    const stackOrder = ['Child', 'Female', 'Male', 'Others'];
    const activeKeys = stackOrder.filter(key => payload[key] > 0);
    const isTop = props.dataKey === activeKeys[activeKeys.length - 1];

    if (isTop) {
        return (
            <path
                d={`M${x},${y + radius} 
            a${radius},${radius} 0 0 1 ${radius},-${radius} 
            h${width - 2 * radius} 
            a${radius},${radius} 0 0 1 ${radius},${radius} 
            v${height - radius} 
            h-${width} Z`}
                fill={fill}
                stroke={stroke}
                strokeWidth={strokeWidth}
            />
        );
    }
    return <rect x={x} y={y} width={width} height={height} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
};

// 3. 📊 MAIN CHART COMPONENT
export const GenderPatientsChart = ({ chartResponse, isLoading, onFilterChange }) => {
    const [selectedFilter, setSelectedFilter] = useState('Current Week');
    const [isOpen, setIsOpen] = useState(false);

    if (isLoading || !chartResponse) {
        return <GraphLoadingSkeleton />;
    }

    const genderData = chartResponse?.gender_wise_chart_data || [];
    const meta = chartResponse?.meta || { weeksAgo: 0, currentMonthName: 'June', isCurrentWeek: true };
    const filterOptions = ['Current Week', 'Current Month', 'Year Wise'];

    const handleFilterSelect = (option) => {
        setSelectedFilter(option);
        setIsOpen(false);
        if (onFilterChange) onFilterChange(option);
    };

    return (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 pb-0 shadow-sm dark:border-slate-800 dark:bg-slate-950 overflow-hidden">

            {/* 🛠️ Top Level Title Panel (No Overlap) */}
           <div className="flex items-start justify-between flex-nowrap gap-x-4 w-full">
    {/* Left Side: Heading & Subtitle */}
    <div className="min-w-0 flex-1">
        <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-400 dark:text-slate-500 font-medium whitespace-nowrap overflow-hidden text-ellipsis">
            {selectedFilter === "Current Week" 
                ? (meta?.isCurrentWeek
                    ? `Gender trends — ${meta?.currentMonthName || 'This Week'}`
                    : `Trends — ${meta?.weeksAgo} Week(s) Ago (${meta?.currentMonthName})`)
                : `Gender trends — ${selectedFilter}`
            }
        </p>
        <h2 className="mt-0.5 text-base sm:text-lg font-bold text-slate-900 dark:text-white whitespace-nowrap overflow-hidden text-ellipsis">
            Demographics
        </h2>
    </div>

    {/* Right Side: Minimal Expandable Filter Dropdown */}
    <div className="relative inline-block text-left z-20 flex-shrink-0">
        <div>
            <button
                onClick={() => setIsOpen(!isOpen)}
                type="button"
                className={`inline-flex items-center justify-center gap-x-1.5 rounded-full bg-slate-50 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800 transition-all duration-300 ${
                    isOpen ? 'w-36 h-9 px-3' : 'w-9 h-9 px-0'
                }`}
            >
                {/* Filter Icon */}
                <svg className="h-4 w-4 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                </svg>

                {/* Text rendered conditionally on expand */}
                {isOpen && (
                    <>
                        <span className="truncate flex-1 text-left animate-fade-in">{selectedFilter}</span>
                        <svg className="-mr-1 h-4 w-4 flex-shrink-0 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                    </>
                )}
            </button>
        </div>
        {isOpen && (
            <div className="absolute right-0 mt-2 w-40 rounded-2xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-slate-900 dark:ring-slate-800 overflow-hidden">
                <div className="py-1.5 px-1 space-y-0.5">
                    {filterOptions.map((option) => (
                        <button
                            key={option}
                            onClick={() => handleFilterSelect(option)}
                            className={`block w-full text-left rounded-xl px-4 py-2 text-xs font-medium transition-colors ${
                                selectedFilter === option
                                    ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white'
                                    : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/50'
                            }`}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            </div>
        )}
    </div>
</div>
<hr className="mt-2" />
            {/* Chart Render Area */}
            <div className="mt-2 h-[360px] w-full mb-[-5px]">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={genderData} barSize={22} margin={{ left: -25, right: 0, top: 20, bottom: -13 }}>
                        <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <YAxis tickLine={false} axisLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <Tooltip
                            contentStyle={{
                                borderRadius: 16,
                                border: "1px solid rgba(255,255,255,0.1)",
                                backgroundColor: "#0f172a",
                                color: "#fff",
                                boxShadow: "0 10px 25px -5px rgba(0,0,0,0.3)"
                            }}
                        />
                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            iconType="circle"
                            iconSize={6}
                            // 🛠️ paddingBottom ko paddingTop kiya taaki x-axis se space mile aur niche chipke nahi
                            wrapperStyle={{ paddingTop: 20, fontSize: 11, marginLeft: 20 }}
                            formatter={(value) => (
                                <span className="text-slate-600 dark:text-slate-400 font-semibold text-xs px-1">
                                    {value}
                                </span>
                            )}
                        />
                        <Bar dataKey="Child" name="Child" fill="rgba(245, 158, 11, 0.6)" stroke="#d97706" strokeWidth={1.5} stackId="gender" shape={<DynamicGlassBar />} />
                        <Bar dataKey="Female" name="Female" fill="rgba(236, 72, 153, 0.6)" stroke="#db2777" strokeWidth={1.5} stackId="gender" shape={<DynamicGlassBar />} />
                        <Bar dataKey="Male" name="Male" fill="rgba(59, 130, 246, 0.6)" stroke="#2563eb" strokeWidth={1.5} stackId="gender" shape={<DynamicGlassBar />} />
                        <Bar dataKey="Others" name="Others" fill="rgba(139, 92, 246, 0.6)" stroke="#7c3aed" strokeWidth={1.5} stackId="gender" shape={<DynamicGlassBar />} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};